import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ShieldCheck, ShieldX, Clock, ChevronLeft, Award, Printer } from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem('nexus_token');
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso || '—'; }
};

const formatVerifyId = (cert) => {
  if (!cert?.verification_uuid) return '(pending)';
  const dt = cert.issued_at ? new Date(cert.issued_at) : new Date();
  const yy = String(dt.getFullYear() % 100).padStart(2, '0');
  const tail = cert.verification_uuid.replace(/-/g, '').slice(-4).toUpperCase();
  return `NexusTraining-${yy}-${tail}`;
};

const STATUS_META = {
  VALID:    { label: 'Verified Valid', icon: ShieldCheck, color: 'text-emerald-300', bg: 'bg-emerald-500/15 border-emerald-500/40' },
  EXPIRED:  { label: 'Expired',        icon: Clock,       color: 'text-amber-300',   bg: 'bg-amber-500/15 border-amber-500/40' },
  REVOKED:  { label: 'Revoked',        icon: ShieldX,     color: 'text-rose-300',    bg: 'bg-rose-500/15 border-rose-500/40' },
};

const CertificateView = () => {
  const { verifyUuid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await apiCall(`/api/training-center/certificates/verify/${verifyUuid}`);
      setData(r);
    } catch (e) { setErr(e.message); toast.error(e.message); }
  }, [verifyUuid]);
  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] text-white flex items-center justify-center p-6" data-testid="cert-error-page">
        <Card className="bg-[#131A2A] border-rose-500/40 max-w-md">
          <CardContent className="p-8 text-center">
            <ShieldX className="w-12 h-12 text-rose-300 mx-auto mb-3" />
            <h1 className="text-lg font-bold text-white">Certificate not found</h1>
            <p className="text-sm text-slate-400 mt-1">{err}</p>
            <Button onClick={() => navigate('/training-center')} className="mt-4 bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold">Back to Training Center</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0A0F1A] text-white p-10 font-mono text-sm">Verifying certificate…</div>;

  const meta = STATUS_META[data.status] || STATUS_META.VALID;
  const StatusIcon = meta.icon;
  const cert = data.certificate;

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white print:bg-white print:text-black" data-testid="cert-view-page">
      <div className="border-b border-[#1E293B] print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => navigate('/training-center')} className="border-slate-600 !text-slate-200 hover:bg-slate-800" data-testid="cert-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Training Center
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="border-amber-500/50 !text-amber-300 hover:bg-amber-500/15 bg-transparent" data-testid="cert-print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Verification banner */}
        <div className={`rounded-md border-2 ${meta.bg} p-3 mb-5 flex items-center gap-2 print:hidden`} data-testid="cert-verify-banner">
          <StatusIcon className={`w-5 h-5 ${meta.color}`} />
          <p className="text-sm font-semibold text-white">{meta.label}</p>
          <p className="ml-auto text-[11px] font-mono text-slate-300 truncate" data-testid="cert-verify-id">Verify ID: {formatVerifyId(cert)}</p>
        </div>

        {/* Certificate canvas */}
        <div className="rounded-2xl border-4 border-amber-400/60 bg-gradient-to-br from-[#0F1B33] via-[#0A0F1A] to-[#0F1B33] p-8 sm:p-12 relative overflow-hidden print:bg-white print:border-amber-700" data-testid="cert-canvas">
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(252,211,77,0.4), transparent 40%), radial-gradient(circle at 80% 20%, rgba(59,130,246,0.4), transparent 40%)'
          }} />
          <div className="relative text-center space-y-5">
            <img src="/training-center/seal.png" alt="" className="w-32 h-32 mx-auto rounded-full shadow-2xl" />
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.32em] text-amber-300 print:text-amber-700">Nexus Investigation Agency · Training Center</p>
              <p className="text-2xl sm:text-3xl font-extrabold text-white print:text-slate-900 mt-2">Certificate of Completion</p>
            </div>
            <p className="text-slate-300 print:text-slate-700 text-sm">This is to certify that</p>
            <p className="text-4xl sm:text-5xl font-bold text-amber-200 print:text-amber-700" style={{ fontFamily: '"Times New Roman", serif' }} data-testid="cert-user-name">
              {cert.user_name}
            </p>
            <p className="text-slate-300 print:text-slate-700 text-sm">has successfully completed the requirements for</p>
            <p className="text-xl sm:text-2xl font-bold text-white print:text-slate-900" data-testid="cert-title">
              {cert.cert_title}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6 text-left text-xs font-mono text-slate-300 print:text-slate-700 max-w-2xl mx-auto">
              <div>
                <p className="uppercase tracking-[0.18em] text-[10px] text-amber-300 print:text-amber-700">Certificate ID</p>
                <p className="font-bold text-white print:text-slate-900 mt-0.5" data-testid="cert-id">{cert.id}</p>
              </div>
              <div>
                <p className="uppercase tracking-[0.18em] text-[10px] text-amber-300 print:text-amber-700">Issued</p>
                <p className="font-bold text-white print:text-slate-900 mt-0.5" data-testid="cert-issued">{fmt(cert.issued_at)}</p>
              </div>
              <div>
                <p className="uppercase tracking-[0.18em] text-[10px] text-amber-300 print:text-amber-700">Expires</p>
                <p className="font-bold text-white print:text-slate-900 mt-0.5" data-testid="cert-expires">{fmt(cert.expires_at)}</p>
              </div>
              {cert.course_ids?.length > 0 && (
                <div className="col-span-2 sm:col-span-3">
                  <p className="uppercase tracking-[0.18em] text-[10px] text-amber-300 print:text-amber-700">Courses</p>
                  <p className="text-white print:text-slate-900 mt-0.5">{cert.course_ids.join(' · ')}</p>
                </div>
              )}
            </div>

            <div className="mt-6 pt-5 border-t border-amber-400/30 print:border-amber-700/40">
              <p className="text-xs font-mono text-slate-400 print:text-slate-600">
                Verify this certificate at any time via{' '}
                <span className="text-amber-300 print:text-amber-700">/training-center/certificate/{formatVerifyId(cert)}</span>
              </p>
              <p className="text-[10px] font-mono text-slate-500 mt-2 print:text-slate-600">{cert.issuer}</p>
            </div>
          </div>
        </div>

        <div className="mt-5 text-center text-xs text-slate-400 font-mono print:hidden">
          <Award className="w-4 h-4 inline mr-1 text-amber-300" /> Certificate is auth-gated — visible only to authenticated NexusTrac users.
        </div>
      </div>
    </div>
  );
};

export default CertificateView;
